/**
 * Projecting a world anchor onto the screen.
 *
 * ## What this replaced, and why it matters
 *
 * This module used to take Euler angles from the backend, spherical bearings
 * from the anchor, subtract them, and map the difference through `tan()`. That
 * is not a perspective projection, and it failed three ways at once:
 *
 *   1. **The horizontal axis was mirrored.** `atan2(dx, dz)` is the wrong
 *      handedness for a camera looking down −z: the correct bearing is
 *      `atan2(dx, -dz)`. Both the anchor's bearing and the camera's used the
 *      flipped form, so yaw *decreased* as a bearing moved right, and the
 *      subtraction carried the opposite sign. Turning right slid anchors right
 *      instead of left — they moved the **wrong way at double speed**, on every
 *      backend, at any tracking quality.
 *   2. **Roll was discarded.** `look()` computed it; nothing read it. A real
 *      capture had a median |roll| of 25°, so this was continuous, not marginal.
 *   3. **Euler subtraction is not projection.** Even level and unmirrored, it
 *      only approximates perspective near the centre of the frame; at the
 *      capture's typical −32° pitch the error reached 76% of screen width for an
 *      anchor 25° off-axis.
 *
 * The fix is to do what AlvaAR's own three.js example does: keep the pose whole,
 * transform the world point into camera space, and divide. All three faults
 * disappear because none of them was ever a separate bug — they were symptoms of
 * not having a view transform at all.
 *
 * ⚠️ Do not "simplify" this back into angle arithmetic. If a future change needs
 * a bearing, derive it here from the projected result; never rebuild the
 * projection from `look()`.
 */

import type { Anchor, CameraPose, Quat, Vec3 } from '../spatial/SpatialBackend.js';
import type { ViewGeometry } from '../spatial/cameraFov.js';

export interface Projected {
  /** left, as vw (0..100). */
  x: number;
  /** top, as vh (0..100). */
  y: number;
  /** angular distance from screen center, degrees. */
  offCenter: number;
  /** Worth drawing at all. False only when far outside the frame. */
  visible: boolean;
  /**
   * Edge fade, 0..1. Multiplies opacity so fragments dissolve at the frame edge
   * instead of popping. See EDGE_FADE.
   */
  edge: number;
  /** depth scale multiplier (near = larger). ~1 at reference distance. */
  scale: number;
  /** 0 (near, opaque) .. 1 (far, hazed) — drives fog opacity. */
  fog: number;
  /** Distance from the camera, in the backend's units. */
  distance: number;
  /**
   * CSS stacking order: nearer is higher. Always a positive integer and
   * strictly ordered by distance at any scale. (`1000 − distance·100`, the
   * formula callers used, goes negative past 10 units — routine at the 0.05-0.1
   * m/unit sessions measured in the field — and then sorts behind the page.)
   */
  zIndex: number;
}

/**
 * Size and fog bands, as MULTIPLES of the anchor's own `placementDistance`.
 *
 * ⚠️ These were absolute SLAM units (REF 3, NEAR 1.2, FAR 8) — the same bug
 * STATUS §17.10 fixed in visual/Text/textPlane.ts. A SLAM unit is arbitrary per
 * session (~7 cm in the 2026-08-25 capture), so a well-placed fragment at 24
 * units rendered at the minimum scale and fully fogged at birth, while one at
 * the raw-unit fallback rendered at full size. Ratios mean the same thing at any
 * scale. They reproduce the old numbers exactly at a 3.2-unit placement (the
 * default `ANCHOR_DEPTH_UNITS`): 3 / 3.2, 1.2 / 3.2, 8 / 3.2.
 */
const REF_RATIO = 3 / 3.2;
const NEAR_RATIO = 1.2 / 3.2;
const FAR_RATIO = 8 / 3.2;
/**
 * The placement distance assumed for anchors that do not report one (VO,
 * orientation shell). Chosen so their behaviour is exactly the old absolute
 * REF 3 / NEAR 1.2 / FAR 8.
 */
const DEFAULT_PLACEMENT_DISTANCE = 3.2;

/**
 * Edge dissolve, as a fraction of the half-frame beyond the boundary.
 *
 * Replaces a hard `display: none` cutoff, which made fragments flicker: one near
 * the boundary flips state on the smallest pose correction, and SLAM produces
 * those constantly (3-4 per 30s run). Because fragments cluster — they are all
 * anchored wherever the participant happened to be standing — rotating past a
 * cluster popped several at once.
 *
 * Expressed as a fraction rather than degrees so it scales with the lens: on a
 * narrow field 9° is most of the frame, on a wide one it is a sliver.
 */
const EDGE_FADE = 0.3;
/** Keep drawing this much further out before hiding, so nothing re-mounts. */
const KEEP_DRAWN = 0.3;

const RAD = 180 / Math.PI;

/** Rotate a vector by a quaternion. */
function rotate(q: Quat, v: Vec3): Vec3 {
  const tx = 2 * (q.y * v.z - q.z * v.y);
  const ty = 2 * (q.z * v.x - q.x * v.z);
  const tz = 2 * (q.x * v.y - q.y * v.x);
  return {
    x: v.x + q.w * tx + (q.y * tz - q.z * ty),
    y: v.y + q.w * ty + (q.z * tx - q.x * tz),
    z: v.z + q.w * tz + (q.x * ty - q.y * tx),
  };
}

const conjugate = (q: Quat): Quat => ({ x: -q.x, y: -q.y, z: -q.z, w: q.w });

export interface CameraSpacePoint {
  /** Camera-space coordinates: +x right, +y up, looking down −z. */
  x: number;
  y: number;
  z: number;
  /** Normalised screen offset, ±1 at the frame edge. */
  nx: number;
  ny: number;
  /** Distance from the camera centre. */
  distance: number;
  /** False when the point is behind the camera. */
  inFront: boolean;
}

/**
 * The view transform: a world point, seen from a camera pose.
 *
 * This is `viewMatrix × worldPoint` written out — rotating the relative vector
 * by the inverse camera orientation — followed by the perspective divide. It is
 * exactly what a `THREE.PerspectiveCamera` does, and deliberately so: the
 * reference implementation for this project's tracker is AlvaAR's three.js
 * example, and matching it removes a whole class of "nearly right" geometry.
 *
 * Exported because it is the one piece worth testing directly against
 * hand-computed ground truth.
 */
export function worldToCamera(world: Vec3, cam: CameraPose, view: ViewGeometry): CameraSpacePoint {
  const rel: Vec3 = {
    x: world.x - cam.position.x,
    y: world.y - cam.position.y,
    z: world.z - cam.position.z,
  };
  // World → camera. The inverse of a unit quaternion is its conjugate.
  const c = rotate(conjugate(cam.orientation), rel);
  const distance = Math.hypot(c.x, c.y, c.z);
  // The camera looks down −z, so anything with z >= 0 is behind it.
  const inFront = c.z < -1e-9;
  const depth = inFront ? -c.z : 0;
  return {
    x: c.x,
    y: c.y,
    z: c.z,
    nx: inFront ? c.x / depth / view.tanHalfX : Infinity,
    ny: inFront ? c.y / depth / view.tanHalfY : Infinity,
    distance,
    inFront,
  };
}

/**
 * @param view Screen geometry from `viewGeometry(videoElement)` — accounts for
 *   the separate horizontal/vertical fields of a portrait frame AND for
 *   `object-fit: cover` cropping. Passing the wrong one here looks exactly like
 *   broken tracking.
 */
export function project(anchor: Anchor, cam: CameraPose, view: ViewGeometry): Projected {
  const p = worldToCamera(anchor.world, cam, view);

  const x = 50 + p.nx * 50;
  // Screen y grows downward, camera y grows upward.
  const y = 50 - p.ny * 50;

  // Angle off the optical axis. Derived from the projected point rather than
  // from bearings, so it cannot disagree with what is drawn.
  const offCenter = p.inFront
    ? Math.atan(Math.hypot(p.nx * view.tanHalfX, p.ny * view.tanHalfY)) * RAD
    : 180;

  // How far outside the frame, as a fraction of the half-frame (0 = at the edge).
  const over = Math.max(Math.abs(p.nx), Math.abs(p.ny)) - 1;
  const edge = !p.inFront ? 0 : over <= 0 ? 1 : Math.max(0, 1 - over / EDGE_FADE);
  const visible = p.inFront && over < EDGE_FADE + KEEP_DRAWN;

  // Against the anchor's own intended distance, not an absolute unit count.
  const placed =
    anchor.placementDistance !== undefined && anchor.placementDistance > 1e-6 && Number.isFinite(anchor.placementDistance)
      ? anchor.placementDistance
      : DEFAULT_PLACEMENT_DISTANCE;
  const refDistance = placed * REF_RATIO;
  const near = placed * NEAR_RATIO;
  const far = placed * FAR_RATIO;

  const distance = p.distance > 0 ? p.distance : refDistance;
  const scale = Math.max(0.55, Math.min(1.6, refDistance / distance));
  const fog = Math.max(0, Math.min(1, (distance - near) / (far - near)));
  // Monotonic in distance, positive, never saturating to a shared value at
  // field distances. 1e6/(1+d): d=0 → 1e6, d=10 → 90909, d=1000 → 999.
  const zIndex = Math.max(1, Math.round(1e6 / (1 + Math.max(0, p.distance))));

  return { x, y, offCenter, visible, edge, scale, fog, distance, zIndex };
}

/**
 * Held = near enough to centre to count as attention.
 *
 * Deliberately unaffected by the edge fade: dwell should depend on where the
 * participant is looking, not on rendering decisions.
 *
 * The tolerance is passed in rather than read from config so this module stays
 * pure geometry with no `import.meta.env` dependency — which is what lets it be
 * tested outside Vite, and this is the module that most needed testing.
 */
export function isHeld(p: Projected, toleranceDeg: number): boolean {
  return p.edge > 0 && p.offCenter < toleranceDeg;
}
